import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { getExploreVisions, getPublicVision } from '../../actions/planningAction';
import './ExploreFeed.css';

const SORTS = [
  { k: 'recent', l: 'Recent' },
  { k: 'oldest', l: 'Oldest' },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const initial = (name = '') => (name.trim()[0] || '?').toUpperCase();
const trunc = (s = '', n) => (s.length > n ? s.slice(0, n).trim() + '…' : s);

const STATUS_LABEL = { standby: 'Standby', active: 'Active', reality: 'Reality' };
const StatusPill = ({ status }) => (
  <span className={`ex-status ex-status--${status || 'standby'}`}>{STATUS_LABEL[status] || 'Standby'}</span>
);

export default function ExploreFeed() {
  const dispatch = useDispatch();

  const [visions, setVisions]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [keyword, setKeyword]       = useState('');
  const [query, setQuery]           = useState('');   // applied search
  const [sort, setSort]             = useState('recent');
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  const [detail, setDetail]         = useState(null);   // { vision, milestones } | 'loading' | null
  const [detailErr, setDetailErr]   = useState(false);

  const load = useCallback(async (p, kw, srt, append) => {
    append ? setLoadingMore(true) : setLoading(true);
    try {
      const d = await dispatch(getExploreVisions({ page: p, keyword: kw, sort: srt }));
      setVisions(prev => append ? [...prev, ...(d.visions || [])] : (d.visions || []));
      setTotalPages(d.totalPages || 1);
      setTotal(d.total || 0);
      setPage(d.page || p);
    } catch (_) {
      if (!append) setVisions([]);
    }
    append ? setLoadingMore(false) : setLoading(false);
  }, [dispatch]);

  useEffect(() => { load(1, query, sort, false); }, [query, sort, load]);

  const submitSearch = (e) => { e.preventDefault(); setQuery(keyword.trim()); };

  const openDetail = async (id) => {
    setDetail('loading'); setDetailErr(false);
    try {
      const d = await dispatch(getPublicVision(id));
      setDetail(d);
    } catch (_) {
      setDetail(null); setDetailErr(true);
    }
  };

  return (
    <div className="ex-page">
      <Helmet>
        <title>Explore · dmyor</title>
        <meta name="description" content="Explore visions people are working to make real on dmyor." />
      </Helmet>

      <header className="ex-head">
        <div className="ex-head-text">
          <h1 className="ex-title">Explore</h1>
          <p className="ex-sub">Visions people are working to make real. {total > 0 && <span className="ex-count">{total.toLocaleString()} public</span>}</p>
        </div>

        <form className="ex-search" onSubmit={submitSearch}>
          <i className="bx bx-search ex-search-icon" />
          <input
            className="ex-search-input"
            placeholder="Search visions…"
            value={keyword}
            onChange={e => setKeyword(e.target.value)}
          />
          {keyword && (
            <button type="button" className="ex-search-clear" onClick={() => { setKeyword(''); setQuery(''); }}>×</button>
          )}
        </form>
      </header>

      <div className="ex-toolbar">
        <div className="ex-sorts">
          {SORTS.map(s => (
            <button
              key={s.k}
              className={`ex-sort${sort === s.k ? ' is-active' : ''}`}
              onClick={() => setSort(s.k)}
            >{s.l}</button>
          ))}
        </div>
        {query && <span className="ex-applied">Results for “{query}”</span>}
      </div>

      {loading ? (
        <div className="ex-grid">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="ex-card ex-card--skel" />)}
        </div>
      ) : visions.length === 0 ? (
        <div className="ex-empty">
          <div className="ex-empty-mark">◎</div>
          <p className="ex-empty-title">{query ? 'No visions match your search.' : 'No public visions yet.'}</p>
          <p className="ex-empty-sub">{query ? 'Try a different search.' : 'Make one of your visions public from its settings to see it here.'}</p>
        </div>
      ) : (
        <>
          <div className="ex-grid">
            {visions.map(v => (
              <button key={v._id} className="ex-card" onClick={() => openDetail(v._id)}>
                <div className="ex-card-top">
                  <div className="ex-card-badges">
                    <span className="ex-badge">{v.timeframe}-year</span>
                    <StatusPill status={v.status} />
                  </div>
                  <span className="ex-card-date">{fmtDate(v.publishedAt)}</span>
                </div>
                <h3 className="ex-card-title">{v.title}</h3>
                {v.description && <p className="ex-card-desc">{trunc(v.description, 150)}</p>}
                <div className="ex-card-foot">
                  <span className="ex-author">
                    <span className="ex-avatar">{initial(v.author)}</span>
                    {v.author}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {page < totalPages && (
            <div className="ex-more-row">
              <button className="ex-more" onClick={() => load(page + 1, query, sort, true)} disabled={loadingMore}>
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail overlay */}
      {detail !== null && (
        <div className="ex-modal-overlay" onMouseDown={() => setDetail(null)}>
          <div className="ex-modal" onMouseDown={e => e.stopPropagation()}>
            <button className="ex-modal-close" onClick={() => setDetail(null)}>×</button>
            {detail === 'loading' ? (
              <div className="ex-modal-loading">Loading…</div>
            ) : detailErr ? (
              <div className="ex-modal-loading">Could not load this vision.</div>
            ) : (
              <>
                <div className="ex-modal-top">
                  <span className="ex-badge">{detail.vision.timeframe}-year vision</span>
                  <StatusPill status={detail.vision.status} />
                  <span className="ex-card-date">{fmtDate(detail.vision.publishedAt)}</span>
                </div>
                <h2 className="ex-modal-title">{detail.vision.title}</h2>
                <div className="ex-modal-author">
                  <span className="ex-avatar">{initial(detail.vision.author)}</span>
                  {detail.vision.author}
                </div>
                {detail.vision.description
                  ? <p className="ex-modal-desc">{detail.vision.description}</p>
                  : <p className="ex-modal-desc ex-modal-desc--empty">No plan description added.</p>}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
